/**
 * Canvas visual effect helpers — pure math functions + canvas drawing utilities.
 * Used by canvas-renderer.ts for enhanced visuals.
 */

/**
 * Returns 0-1 sine wave for pulsing animations.
 * Uses sin shifted to 0-1 range: (sin(t * 2π / period) + 1) / 2.
 */
export function computePulseAlpha(timestamp: number, periodMs: number): number {
  if (periodMs <= 0) return 0.5;
  const phase = (timestamp / periodMs) * Math.PI * 2;
  return (Math.sin(phase) + 1) / 2;
}

/**
 * Computes three points forming an arrowhead triangle.
 * Arrow points from (fromX,fromY) toward (toX,toY).
 */
export function computeArrowHeadPoints(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  headLength: number,
): {
  tip: { x: number; y: number };
  p1: { x: number; y: number };
  p2: { x: number; y: number };
} {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  const spread = Math.PI / 6;

  return {
    tip: { x: toX, y: toY },
    p1: {
      x: toX - headLength * Math.cos(angle - spread),
      y: toY - headLength * Math.sin(angle - spread),
    },
    p2: {
      x: toX - headLength * Math.cos(angle + spread),
      y: toY - headLength * Math.sin(angle + spread),
    },
  };
}

/**
 * Linearly interpolates between two hex colors.
 * Supports "#rgb" and "#rrggbb" formats.
 */
export function interpolateColor(
  color1: string,
  color2: string,
  t: number,
): string {
  const c1 = parseHex(color1);
  const c2 = parseHex(color2);
  const clamped = Math.max(0, Math.min(1, t));

  const r = Math.round(c1.r + (c2.r - c1.r) * clamped);
  const g = Math.round(c1.g + (c2.g - c1.g) * clamped);
  const b = Math.round(c1.b + (c2.b - c1.b) * clamped);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export interface IntensityPalette {
  readonly weak?: string;
  readonly mid?: string;
  readonly strong?: string;
}

const INTENSITY_PALETTE_DEFAULT: Required<IntensityPalette> = {
  weak: "#22c55e",
  mid: "#f59e0b",
  strong: "#ef4444",
};

/**
 * Maps 0-1 intensity to weak → mid → strong gradient. Defaults match the
 * baseline green/orange/red ramp; pass `palette` to thread theme tokens
 * (`--cn-asf-aim-weak/-mid/-strong`) instead.
 */
export function computeIntensityColor(
  intensity: number,
  palette?: IntensityPalette,
): string {
  const weak = palette?.weak ?? INTENSITY_PALETTE_DEFAULT.weak;
  const mid = palette?.mid ?? INTENSITY_PALETTE_DEFAULT.mid;
  const strong = palette?.strong ?? INTENSITY_PALETTE_DEFAULT.strong;
  const clamped = Math.max(0, Math.min(1, intensity));
  if (clamped <= 0.5) {
    return interpolateColor(weak, mid, clamped * 2);
  }
  return interpolateColor(mid, strong, (clamped - 0.5) * 2);
}

/* ── Canvas drawing helpers (depend on CanvasRenderingContext2D) ── */

export function createRadialGradient(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  innerColor: string,
  outerColor: string,
): CanvasGradient {
  const gradient = ctx.createRadialGradient(
    cx,
    cy,
    innerRadius,
    cx,
    cy,
    outerRadius,
  );
  gradient.addColorStop(0, innerColor);
  gradient.addColorStop(1, outerColor);
  return gradient;
}

export function drawShadowedCircle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  fillStyle: string | CanvasGradient,
  shadow: { blur: number; color: string; offsetX?: number; offsetY?: number },
): void {
  ctx.save();
  ctx.shadowBlur = shadow.blur;
  ctx.shadowColor = shadow.color;
  ctx.shadowOffsetX = shadow.offsetX ?? 0;
  ctx.shadowOffsetY = shadow.offsetY ?? 2;

  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fillStyle = fillStyle;
  ctx.fill();
  ctx.restore();
}

export function drawGlowRing(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  color: string,
  pulseAlpha: number,
): void {
  ctx.save();
  ctx.globalAlpha = 0.3 + pulseAlpha * 0.5;
  ctx.beginPath();
  ctx.arc(cx, cy, radius + 2 + pulseAlpha * 2, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

/* ── Internal helpers ── */

function parseHex(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace("#", "");
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(clean)) {
    return { r: 0, g: 0, b: 0 };
  }
  if (clean.length === 3) {
    const r = clean.charAt(0);
    const g = clean.charAt(1);
    const b = clean.charAt(2);
    return {
      r: Number.parseInt(r + r, 16),
      g: Number.parseInt(g + g, 16),
      b: Number.parseInt(b + b, 16),
    };
  }
  return {
    r: Number.parseInt(clean.slice(0, 2), 16),
    g: Number.parseInt(clean.slice(2, 4), 16),
    b: Number.parseInt(clean.slice(4, 6), 16),
  };
}

function toHex(n: number): string {
  return n.toString(16).padStart(2, "0");
}
